import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { CheckIconComponent } from '../../../@icons/check-icon.component';

/** Une étape : faite (coche), la suivante (cerclée), ou à faire. */
export interface StepListItem {
  /** Texte déjà traduit. */
  label: string;
  detail?: string;
  state: 'done' | 'next' | 'todo';
  /** Une étape en plus, à faire quand on veut (alertes, installer l'app) : plus petite, plus discrète. */
  discreet?: boolean;
}

/**
 * Une suite d'étapes courtes (`vw-step-list`) : le numéro dans un rond, la
 * coche quand c'est fait, un cercle plus marqué pour la suivante. Pas de barre
 * ni de pourcentage : trois étapes se lisent d'un coup d'œil. Chaque étape est
 * un bouton (`stepClick` donne son rang) ; le lecteur d'écran entend son état.
 * Usage : `<vw-step-list [steps]="steps" (stepClick)="go($event)" />`
 */
@Component({
  selector: 'vw-step-list',
  standalone: true,
  imports: [NgClass, TranslatePipe, CheckIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <ol class="flex flex-col divide-y divide-vw-line" data-test="step-list">
      @for (step of steps; track $index) {
        <li>
          <button type="button" class="flex min-h-[52px] w-full items-center gap-3 py-1.5 text-left" (click)="stepClick.emit($index)"
            [attr.aria-current]="step.state === 'next' ? 'step' : null" [attr.data-state]="step.state">
            <span class="flex shrink-0 items-center justify-center rounded-pill font-bold" aria-hidden="true"
              [class.mx-1]="step.discreet" [class.h-6]="step.discreet" [class.w-6]="step.discreet" [class.text-caption]="step.discreet"
              [class.h-8]="!step.discreet" [class.w-8]="!step.discreet" [class.text-body-s]="!step.discreet"
              [ngClass]="{
                'bg-vw-primary text-vw-on-color': step.state === 'done',
                'bg-vw-surface text-vw-primary-ink ring-2 ring-inset ring-vw-primary': step.state === 'next',
                'bg-vw-surface text-vw-muted ring-1 ring-inset ring-vw-line-strong': step.state === 'todo'
              }">
              @if (step.state === 'done') {
                <vw-check-icon class="block h-[18px] w-[18px] [&_svg]:h-full [&_svg]:w-full"></vw-check-icon>
              } @else {
                {{ $index + 1 }}
              }
            </span>
            <span class="flex min-w-0 flex-col">
              <span class="font-semibold" [ngClass]="[step.discreet ? 'text-body-s' : 'text-body', step.state === 'done' || step.discreet ? 'text-vw-body' : 'text-vw-ink']">
                {{ step.label }}
                @if (step.state === 'done') {
                  <span class="sr-only">, {{ 'components.step-list.done' | translate }}</span>
                }
              </span>
              @if (step.detail) {
                <span class="text-body-s text-vw-muted">{{ step.detail }}</span>
              }
            </span>
          </button>
        </li>
      }
    </ol>
  `,
})
export class StepListComponent {
  @Input() steps: StepListItem[] = [];
  @Output() stepClick = new EventEmitter<number>();
}
